import { useCallback, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";

import { Modal } from "@/components/Modals/Modal";
import { Conversation } from "@/pages/Private/pages/Apollo/schema/get-people";
import { ListPersonSchedule } from "@/pages/Private/pages/List/schema/list";
import { useEditScheduledMessageMutation } from "@/pages/Private/redux/schedule/schedule.api";
import { useSnackBar } from "@/utils/useSnackbar";

import { EditScheduledMessageForm } from "../components/EditScheduledMessageForm";

import {
	buildEditScheduledMessagePatch,
	EditScheduledMessageForm as EditForm,
	EditScheduledMessageOriginal,
	isEmptyEditPatch,
	timingFromParkedDelay,
	timingFromScheduledFor,
} from "./editScheduledMessage.util";
import { INVITE_NOTE_PREMIUM_LIMIT } from "./inviteNote.constants";
import { isScheduleWaitingForInvite } from "./resolveSchedule.util";

interface EditTarget {
	schedule: ListPersonSchedule;
	conversation: Conversation;
}

const LINKEDIN_INVITE_STEP_TYPE = "linkedinInviteNode";

/**
 * Edit-in-place flow for one scheduled manual message (PREC-4220), the sibling of
 * `useCancelScheduleModal` and `usePauseScheduleModal`: self-contained, rendered by the bubble's
 * controls. Opens with the message as it is, sends only what changed (`PATCH schedule/:id/message`),
 * and the slice mirrors the answer — schedule row and draft conversation — so the bubble updates
 * without a refetch.
 */
export const useEditScheduleModal = ({
	portalContainer,
}: {
	portalContainer?: HTMLElement | null;
}) => {
	const { t } = useTranslation();
	const { handleOpenSnackbar, renderMessageSnackbar } = useSnackBar();
	const [target, setTarget] = useState<EditTarget | null>(null);
	const [form, setForm] = useState<EditForm | null>(null);
	const [editScheduledMessage, { isLoading }] = useEditScheduledMessageMutation();

	const original = useMemo<EditScheduledMessageOriginal | null>(
		() =>
			target
				? {
						message: target.conversation.message ?? "",
						subject: target.conversation.subject ?? "",
						scheduledFor: target.schedule.scheduledFor,
						scheduledInDays: target.schedule.scheduledInDays ?? null,
					}
				: null,
		[target]
	);

	const requestEdit = useCallback((schedule: ListPersonSchedule, conversation: Conversation) => {
		setTarget({ schedule, conversation });
		setForm({
			message: conversation.message ?? "",
			subject: conversation.subject ?? "",
			timing: schedule.scheduledFor
				? timingFromScheduledFor(schedule.scheduledFor, new Date())
				: timingFromParkedDelay(schedule.scheduledInDays ?? null),
		});
	}, []);

	const close = () => {
		setTarget(null);
		setForm(null);
	};

	const isInviteNote = target?.schedule.stepType === LINKEDIN_INVITE_STEP_TYPE;
	const patch =
		target && original && form
			? buildEditScheduledMessagePatch(target.schedule.id, original, form, new Date())
			: null;
	const invalid =
		!form ||
		(isInviteNote
			? form.message.length > INVITE_NOTE_PREMIUM_LIMIT
			: form.message.trim().length === 0);

	const handleSave = useCallback(async () => {
		if (!patch || isEmptyEditPatch(patch)) {
			return;
		}

		try {
			await editScheduledMessage(patch).unwrap();
			handleOpenSnackbar(t("outbox.editSchedule.success"));
		} catch {
			handleOpenSnackbar(t("outbox.editSchedule.error"));
		} finally {
			close();
		}
	}, [patch, editScheduledMessage, handleOpenSnackbar, t]);

	const renderEditScheduleModal = () => (
		<>
			{target && form && (
				<Modal
					disableSave={invalid || !patch || isEmptyEditPatch(patch)}
					handleClose={close}
					handleSave={handleSave}
					isLoading={isLoading}
					isOpened={true}
					size="sm"
					title={t("outbox.editSchedule.title")}
				>
					<EditScheduledMessageForm
						isInviteNote={isInviteNote}
						isMail={target.conversation.type === "mail_scheduled"}
						isParked={isScheduleWaitingForInvite(target.schedule)}
						message={form.message}
						portalContainer={portalContainer}
						subject={form.subject}
						timing={form.timing}
						onMessageChange={(message) => setForm((f) => (f ? { ...f, message } : f))}
						onSubjectChange={(subject) => setForm((f) => (f ? { ...f, subject } : f))}
						onTimingChange={(timing) => setForm((f) => (f ? { ...f, timing } : f))}
					/>
				</Modal>
			)}
			{renderMessageSnackbar()}
		</>
	);

	return { requestEdit, renderEditScheduleModal };
};
